import {
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@herit-fe/heritage-ui';
import { UI_TEXT } from '@/shared/languages';
import { SelectDrawerField } from './select-drawer-field';
import { useIsCompact } from './use-is-compact';

export interface SelectOption {
  value: string;
  label: string;
  /** 선택 불가(회색 처리) — 예: 조회조건에 목표 없는 항목. */
  disabled?: boolean;
}

/** 옵션 라벨 해석 — value 매칭 옵션의 label, 없으면 value 폴백. 코드→표시명 공통. */
export function labelOf(options: SelectOption[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

/** 필터 "전체"(중립) 옵션 sentinel — base-ui Select 가 빈 문자열 value 를 싫어해 'all' 로 둔다. */
export const ALL_OPTION_VALUE = 'all';

/** 옵션 앞에 "전체"(중립) 옵션 prepend — 필터 SelectField 공통(`UI_TEXT.FILTER_ALL`='전체'). */
export function withAllOption(options: SelectOption[]): SelectOption[] {
  return [{ value: ALL_OPTION_VALUE, label: UI_TEXT.FILTER_ALL }, ...options];
}

/** 연도 드롭다운 옵션 — 올해부터 과거 16년(연 단위 기간 SelectField 공통, 전용 YearField 도입 전). */
export const YEAR_OPTIONS: SelectOption[] = Array.from({ length: 16 }, (_, i) => {
  const year = String(new Date().getFullYear() - i);
  return { value: year, label: year };
});

export interface SelectFieldProps {
  /** 트리거 id — `FieldLabel htmlFor` 와 연결해 라벨↔컨트롤 접근성 결합. */
  id?: string;
  /**
   * 필드명(= `FieldLabel` 문구) — **compact 드로워 헤더**로 쓰인다. 화면엔 `FieldLabel` 이 이미 있으므로
   * 시각 중복이 아니라 오버레이 안 맥락 유지용. 생략 시 `aria-label` → 기본값 순으로 폴백.
   */
  label?: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'default';
  /** 가시 라벨이 없을 때(예: 헤더 인라인 select) 트리거 a11y 라벨. */
  'aria-label'?: string;
}

/**
 * 단일 선택 — heritage `Select` 합성. base-ui `Select.Value` 는 기본적으로 **value 를 노출**하므로
 * 선택 항목의 **label 을 children 으로 명시**해야 라벨이 보인다. 라벨 매핑을 한곳에
 * 캡슐화해 호출처가 `options`/`value`/`onChange` 만 신경 쓰게 한다.
 *
 * **표면은 반응형**(경계 `lg`=1024, `useIsCompact`) — pc 는 이 팝오버 드롭다운, 비-pc 는 bottom `Drawer`
 * (`SelectDrawerField`). 분기는 여기 한 곳뿐이라 호출처는 브레이크포인트를 몰라도 된다(props 동일).
 * 선택 상태는 부모(`value`/`onChange`)가 소유하므로 리사이즈로 표면이 바뀌어도 값이 유실되지 않는다.
 */
export function SelectField(props: SelectFieldProps) {
  const isCompact = useIsCompact();
  if (isCompact) return <SelectDrawerField {...props} />;
  return <SelectPopoverField {...props} />;
}

/** pc 표면 — heritage `Select`(base-ui 팝오버). 앵커 폭 정렬 드롭다운. */
function SelectPopoverField({
  id,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  size,
  'aria-label': ariaLabel,
}: SelectFieldProps) {
  const selected = options.find((option) => option.value === value);
  // 옵션에 없는 값(빈 문자열·sentinel 등 미선택)은 null 로 정규화 → base-ui 가 placeholder 표시.
  // (정규화 안 하면 매칭 없는 raw value 가 그대로 노출됨 — 예: '__unset__'.) 선택 시 실제 value 콜백.
  const current = selected ? value : null;
  return (
    <Select value={current} onValueChange={(next) => onChange(String(next))} disabled={disabled}>
      <SelectTrigger
        id={id}
        size={size}
        aria-label={ariaLabel}
        className={cn(className ?? 'w-full')}
      >
        <SelectValue placeholder={placeholder}>{selected?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
